import { afterRenderEffect, Directive, input, signal } from '@angular/core';
import { injectElementRef } from '@df/ui/internal/element-ref';
import uniqueId from 'lodash-es/uniqueId';
import type { UiAccordionDirective } from '../accordion.directive';
import { injectUiAccordionState } from '../accordion.state';
import { injectUiAccordionItemState } from '../item/accordion-item.state';

/**
 * Apply the `uiAccordionContent` directive to an element that represents the content of an accordion item
 */
@Directive({
    selector: '[uiAccordionContent]',
    exportAs: 'uiAccordionContent',
    host: {
        role: 'region',
        '[id]': 'id()',
        '[attr.data-orientation]': 'accordion().orientation()',
        '[attr.data-open]': 'accordionItem().open() ? "" : null',
        '[attr.data-closed]': 'accordionItem().open() ? null : ""',
        '[attr.aria-labelledby]': 'accordionItem().triggerId()',
        '[style.--ui-accordion-content-width.px]': 'width()',
        '[style.--ui-accordion-content-height.px]': 'height()'
    }
})
export class UiAccordionContentDirective<T> {
    /**
     * The ID of the content region
     */
    readonly id = input<string>(uniqueId('ui-accordion-content'));

    /**
     * Access the accordion
     */
    protected readonly accordion = injectUiAccordionState<UiAccordionDirective<T>>();

    /**
     * Access the accordion item
     */
    protected readonly accordionItem = injectUiAccordionItemState();

    /**
     * Access the accordion content element reference
     */
    private readonly elementRef = injectElementRef();

    /**
     * The content width
     */
    readonly width = signal<number>(0);

    /**
     * The content height
     */
    readonly height = signal<number>(0);

    constructor() {
        this.accordionItem().content.set(this);

        afterRenderEffect(() => {
            if (this.accordionItem().open()) {
                this.width.set(this.elementRef.nativeElement.scrollWidth);
                this.height.set(this.elementRef.nativeElement.scrollHeight);
            }
        });
    }
}
